import {getShopId} from "../components/lib/getShopId";
import formatCurrency from "./formatCurrency";

type VoucherRuleType = {
  enabled: boolean;
  amount: number;
  intlUnit: "currency" | "percent";
  minOrderValueFloat: number;
};

type SalesChannelType = 100001 | 100005 | 100008 | 100009 | 100017 | 100020 | 100022 | 100027 | 100029;

type VoucherRuleObjectType = {
  [key in SalesChannelType]: VoucherRuleType;
};

/**
 * `disabled`: Template for disabled vouchers. All values ignored, when `enabled: false` <br>
 * `elevenPercentNinetyNineMinOrder`: Template giving 11 % with min order value of 99 currency units <br>
 * 'tenBucksSeventyNineMinOrder': Template giving 10 Bucks in the currency of the current shop/sales channel, with a min order value of 79 bucks
 */
const presets: {[key in "disabled" | "elevenPercentNinetyNineMinOrder" | "tenBucksSeventyNineMinOrder"]: VoucherRuleType} = {
  disabled: {
    enabled: false,
    amount: 0,
    intlUnit: "percent",
    minOrderValueFloat: 0.0,
  },
  elevenPercentNinetyNineMinOrder: {
    enabled: true,
    amount: 0.11,
    intlUnit: "percent",
    minOrderValueFloat: 99.0,
  },
  tenBucksSeventyNineMinOrder: {
    enabled: true,
    amount: 10,
    intlUnit: "currency",
    minOrderValueFloat: 79.0,
  },
};

const voucherRules: VoucherRuleObjectType = {
  // DE
  100001: {
    ...presets.elevenPercentNinetyNineMinOrder,
  },
  // COM
  100005: {
    ...presets.disabled,
  },
  // FR
  100008: {
    ...presets.disabled,
  },
  // AT
  100009: {
    ...presets.elevenPercentNinetyNineMinOrder,
  },
  // CH_DE
  100017: {
    ...presets.elevenPercentNinetyNineMinOrder,
  },
  // UK
  100020: {
    ...presets.disabled,
  },
  // DK
  100022: {
    ...presets.disabled,
  },
  // CH_FR
  100027: {
    ...presets.disabled,
  },
  // NL
  100029: {
    ...presets.disabled,
  },
};

class RegistrationVoucher {
  private readonly voucherRule: VoucherRuleType | null;
  private readonly percentFormatter: Intl.NumberFormat;
  public readonly amountFormatted: string | null;
  public readonly minOrderValueFormatted: string | null;

  constructor() {
    const vr = voucherRules[getShopId() as SalesChannelType];
    this.voucherRule = vr.enabled ? vr : null;

    this.percentFormatter = new Intl.NumberFormat([], {
      style: "percent",
    });

    this.amountFormatted = this.formatAmountByUnit();

    if (this.rule) {
      this.minOrderValueFormatted = formatCurrency({
        value: this.rule.minOrderValueFloat,
        fractionDigits: 0,
      });
    } else {
      this.minOrderValueFormatted = null;
    }
  }

  public get rule(): VoucherRuleType | null {
    return this.voucherRule;
  }

  public get enabled(): boolean {
    return !!this.rule;
  }

  public get minOrderValue(): number {
    return this.rule?.minOrderValueFloat ?? 0.0;
  }

  private formatAmountByUnit(): string | null {
    if (!this.rule) {
      return null;
    }

    if (this.rule.intlUnit === "percent") {
      return this.percentFormatter.format(this.rule.amount);
    }

    return formatCurrency({
      value: this.rule.amount,
      fractionDigits: 0,
    });
  }
}

const registrationVoucher = new RegistrationVoucher();
export {registrationVoucher as RegistrationVoucher};
